import { Injectable } from '@angular/core';

import { Competition as CompetitionTag, TagCount, TagType } from '@cds/betting-offer/tags';
import { EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { ApiService } from '@frontend/sports/common/api-utils';
import { FavouritesConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportsProductUtils } from '@frontend/sports/host-app/sports-product/feature/utils';
import { ToasterService } from '@frontend/sports/toaster/feature';
import { UserService } from '@frontend/sports/user/feature';
import { Page } from '@frontend/vanilla/core';
import produce from 'immer';
import { maxBy } from 'lodash-es';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subject,
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    of,
    shareReplay,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';

import { FavouritesFixturesService } from './favourites-fixtures.service';
import { FavouritesModelFactory } from './favourites-model.factory';
import { FavouriteType, FavouritesResponse, FavouritesViewModel } from './favourites.model';

/* eslint-disable no-param-reassign */
export enum FavouriteChangeType {
    Add = 'Add',
    Remove = 'Remove',
    Get = 'Get',
    Save = 'Save',
}

export interface FavouriteChange {
    favourites: FavouritesViewModel[];
    changeType: FavouriteChangeType;
}

type Operation = {
    id: number;
    type: FavouriteChangeType;
    items: FavouritesViewModel[];
};

type CompletedOperation = {
    id: number;
    result: 'success' | 'error';
};

@Injectable({ providedIn: 'root' })
export class FavouritesService {
    private favourites: FavouritesViewModel[] = [];
    private cached: Observable<FavouritesViewModel[]>;
    private default: boolean;

    private eventSubject$ = new BehaviorSubject<FavouriteChange>({
        favourites: [],
        changeType: FavouriteChangeType.Get,
    });
    private pendingOperations$ = new Subject<Operation>();
    private completedOperations$ = new Subject<CompletedOperation>();

    constructor(
        private apiService: ApiService,
        private userService: UserService,
        private page: Page,
        private favouritesConfig: FavouritesConfig,
        private sitecore: Sitecore,
        private favouritesData: FavouritesFixturesService,
        private favouritesFactory: FavouritesModelFactory,
        private toaster: ToasterService,
        productUtils: SportsProductUtils,
    ) {
        this.pendingOperations$
            .pipe(
                concatMap((operation) => {
                    const [updated, updatedFavourites] = this.applyOperation(this.favourites, operation);

                    if (!updated) {
                        this.completedOperations$.next({ id: operation.id, result: 'success' });

                        return EMPTY;
                    }

                    return this.update(updatedFavourites).pipe(
                        tap(() => {
                            this.set(updatedFavourites);
                            this.completedOperations$.next({ id: operation.id, result: 'success' });
                            this.eventSubject$.next({ favourites: this.favourites, changeType: operation.type });
                        }),
                        catchError(() => {
                            this.completedOperations$.next({ id: operation.id, result: 'error' });

                            return EMPTY;
                        }),
                    );
                }),
            )
            .subscribe();

        const get = () => this.getList(true).subscribe();
        this.userService.onUserLogin$.pipe(productUtils.filterSportsEmitLast()).subscribe(get);
        get();
    }

    private applyOperation(favouritesSource: FavouritesViewModel[], operation: Operation): [boolean, FavouritesViewModel[]] {
        const itemsMap = new Map(operation.items.map((item) => [this.getKey(item), item]));
        const favouritesMap = new Map(favouritesSource.map((item, index) => [this.getKey(item), index]));

        let favouritesCopy = [...favouritesSource];
        let favouritesUpdated = false;

        switch (operation.type) {
            case FavouriteChangeType.Add:
                for (const [key, item] of itemsMap.entries()) {
                    if (!favouritesMap.has(key)) {
                        favouritesCopy.push(item);
                        favouritesUpdated = true;
                    }
                }
                break;

            case FavouriteChangeType.Remove:
                favouritesCopy = favouritesSource.filter((item) => !itemsMap.has(this.getKey(item)));
                favouritesUpdated = favouritesCopy.length !== favouritesSource.length;
                break;

            case FavouriteChangeType.Save:
                favouritesCopy = [...itemsMap.values()];

                if (itemsMap.size !== favouritesMap.size) {
                    favouritesUpdated = true;
                    break;
                }

                //needs to be removed when the order is cleanup
                for (const [key, item] of itemsMap.entries()) {
                    const originalIndex = favouritesMap.get(key);
                    if (
                        originalIndex !== undefined &&
                        (item.upcomingOrder !== favouritesSource[originalIndex].upcomingOrder || item.order !== favouritesSource[originalIndex].order)
                    ) {
                        favouritesUpdated = true;
                        break;
                    }
                }

                for (const key of itemsMap.keys()) {
                    if (!favouritesMap.has(key)) {
                        favouritesUpdated = true;
                        break;
                    }
                }
                break;
        }

        return [favouritesUpdated, favouritesUpdated ? favouritesCopy : favouritesSource];
    }

    get change(): Observable<FavouriteChange> {
        return this.eventSubject$;
    }

    get favouritesList(): FavouritesViewModel[] {
        return this.favourites;
    }

    get isDefault(): boolean {
        return this.default;
    }

    get isFull(): boolean {
        return this.favourites.length >= this.favouritesConfig.maxFavourites;
    }

    notify(message: string, iconClass?: string): void {
        if (this.favouritesConfig.isFavouritesEnabled) {
            this.toaster.open(message, {
                duration: this.favouritesConfig.toastMessageTimeOut,
                icon: iconClass,
                panelClass: 'favourites-notification',
            });
        }
    }

    add(itemId: string, sportId: number, type: FavouriteType, name: string, participantImage?: EventParticipantImage): Observable<void> {
        if (this.isFull) {
            return throwError(
                () =>
                    new Error(this.sitecore.favouritesMessages.TooManyFavourites.replace('{{maxCount}}', `(${this.favouritesConfig.maxFavourites})`)),
            );
        }

        const favourite = this.get(itemId, sportId, type);

        if (favourite) {
            return EMPTY;
        }

        const favForSport = this.favourites.find((f) => f.sport.id === sportId && f.upcomingOrder !== 0);
        const maxUpcomingOrder = maxBy(this.favourites.map((f) => f.upcomingOrder || 0));
        const maxOrder = maxBy(this.favourites.map((f) => f.order || 0));
        const newFavourite = this.favouritesFactory.toModel({
            sport: { id: sportId, name: '' },
            itemId,
            name,
            lang: this.page.culture,
            type,
            order: maxOrder ? maxOrder + 1 : 0,
            upcomingOrder: favForSport?.upcomingOrder || (maxUpcomingOrder ? maxUpcomingOrder + 1 : 0),
            participantImage,
        });

        return this.loadCounts([newFavourite], true).pipe(
            mergeMap((updatedFavourite) => this.triggerUpdate(updatedFavourite, FavouriteChangeType.Add)),
        );
    }

    save(items: FavouritesViewModel[]): Observable<void> {
        return this.loadCounts(items, true).pipe(mergeMap((updatedItems) => this.triggerUpdate(updatedItems, FavouriteChangeType.Save)));
    }

    remove(item: FavouritesViewModel | FavouritesViewModel[]): Observable<void> {
        const removedItems = Array.isArray(item) ? item : [item];

        return this.triggerUpdate(removedItems, FavouriteChangeType.Remove);
    }

    get(itemId: string, sportId: number, type: FavouriteType): FavouritesViewModel | undefined {
        return this.favourites.find((favourite) => favourite.sport.id === sportId && favourite.itemId === itemId && favourite.type === type);
    }

    private getKey({ itemId, sport, type }: FavouritesViewModel): string {
        return `${itemId}-${sport.id}-${type}`;
    }

    getList(refresh?: boolean): Observable<FavouritesViewModel[]> {
        if (!this.cached || refresh) {
            this.default = !this.userService.isAuthenticated;

            this.cached = this.apiService.get<FavouritesResponse>('favourites').pipe(
                switchMap((result) => {
                    this.default = result.isDefault;
                    this.favourites = result.favourites.map((current) => this.favouritesFactory.toModel(current));

                    return this.loadCounts(this.favourites);
                }),
                map((favourites) => {
                    this.favourites = favourites;

                    const expired: FavouritesViewModel[] = [];

                    for (const favourite of this.favourites) {
                        if (this.isExpiredFixtureFavourite(favourite)) {
                            expired.push(favourite);
                        }
                    }

                    this.eventSubject$.next({ favourites: this.favourites, changeType: FavouriteChangeType.Get });
                    this.triggerUpdate(expired, FavouriteChangeType.Remove);

                    return this.favourites;
                }),
                shareReplay({
                    bufferSize: 1,
                    refCount: true,
                }),
            );
        }

        return this.cached;
    }

    private loadCounts(favourites: FavouritesViewModel[], loadAdditionalData = false): Observable<FavouritesViewModel[]> {
        return this.favouritesData.getCounts(favourites).pipe(
            map((result) => {
                if (!result) {
                    return favourites;
                }

                return produce(favourites, (favDraft) => {
                    favDraft.forEach((f) => {
                        const tags = result[f.id];

                        if (!tags?.length) {
                            return;
                        }

                        const tag = this.findTag(tags, TagType.Tournament) || this.findTag(tags, TagType.Region) || tags[0];
                        const sportTag = this.findTag(tags, TagType.Sport);
                        const competitionTag = this.findTag(tags, TagType.Competition);
                        // if it's a participant, the correct count is on the Sport tag otherwise any tag should have the correct count
                        const countTag =
                            f.type === FavouriteType.Participant || f.type === FavouriteType.ParticipantV2 ? sportTag || tags[0] : tags[0];

                        if (loadAdditionalData) {
                            if (f.type === FavouriteType.League && !f.region) {
                                f.region = {
                                    id: tag.tag.id,
                                    name: tag.tag.name.value,
                                };
                                if (competitionTag) {
                                    f.imageProfile = (competitionTag.tag as CompetitionTag).imageProfile;
                                }
                            }
                            if (sportTag && !f.sport.name) {
                                f.sport.name = sportTag.tag.name.value;
                            }
                        }
                        f.count = countTag.live + countTag.preMatch;
                    });
                });
            }),
        );
    }

    private set(favourites: FavouritesViewModel[]): void {
        this.favourites = favourites;
        this.cached = of(this.favourites).pipe(
            shareReplay({
                bufferSize: 1,
                refCount: true,
            }),
        );
    }

    private findTag(tags: TagCount[], type: TagType): TagCount | undefined {
        return tags.find((t) => t.tag.type === type);
    }

    private isExpiredFixtureFavourite(favourite: FavouritesViewModel): boolean {
        return favourite.count === 0 && [FavouriteType.Fixture, FavouriteType.FixtureV2].includes(favourite.type);
    }

    private triggerUpdate(items: FavouritesViewModel[], type: FavouriteChangeType) {
        if (!this.userService.isAuthenticated) {
            return throwError(() => new Error(this.sitecore.favouritesMessages.NotAuthenticated));
        }

        const id = Date.now();
        this.pendingOperations$.next({ id, items, type });

        return this.completedOperations$.pipe(
            filter((current) => current.id === id),
            take(1),
            mergeMap(({ result }) => {
                switch (result) {
                    case 'error':
                        return throwError(() => new Error('favourite saving error'));
                    case 'success':
                        return of(void 0);
                }
            }),
        );
    }

    private update(updated: FavouritesViewModel[]) {
        const favourites = updated.map((item) => this.favouritesFactory.toDto(item));

        return this.apiService.post('favourites', { favourites });
    }
}
